import { inject, Injectable } from '@angular/core';
import { FEEDBACK_API_TOKEN_STORAGE_KEY } from '../tokens/token-storage-key.token';

/**
 * Seconds before a token's `exp` at which it is already treated as expired, so
 * a token that would die mid-flight is replaced before it is ever sent.
 */
const EXPIRY_SKEW_SECONDS = 30;

/**
 * Reads/writes the Feedback API bearer token in localStorage under the
 * configured key (default `id_token_delegate_feedback-api`).
 *
 * Use `set()` after obtaining a token (e.g. from `FeedbackRoundService.getToken`
 * or `validateFeedbackRound`); the auth interceptor reads it via `get()`.
 * All access is guarded so it is safe in non-browser (SSR) contexts.
 *
 * `get()` reports an expired token as absent. Callers therefore treat "no
 * usable token" and "no token at all" identically — which is what makes a
 * presence check such as the app's auth guard fetch a replacement instead of
 * reusing a long-dead token for every request.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackApiTokenStore {
  private readonly storageKey = inject(FEEDBACK_API_TOKEN_STORAGE_KEY);

  /** The stored token, or null when none is stored or the stored one has expired. */
  get(): string | null {
    const storedToken = this.read();
    if (storedToken === null) {
      return null;
    }
    return isExpired(storedToken) ? null : storedToken;
  }

  set(token: string): void {
    try {
      globalThis.localStorage?.setItem(this.storageKey, token);
    } catch {
      // localStorage unavailable (SSR / disabled) — nothing to persist.
    }
  }

  clear(): void {
    try {
      globalThis.localStorage?.removeItem(this.storageKey);
    } catch {
      // localStorage unavailable — nothing to clear.
    }
  }

  private read(): string | null {
    try {
      return globalThis.localStorage?.getItem(this.storageKey) ?? null;
    } catch {
      return null;
    }
  }
}

/**
 * True only when the token is a JWT whose `exp` has passed (within the skew).
 * Anything unparseable is reported as usable: the store must not discard a
 * token shape it does not recognize — only the API decides what is valid.
 */
function isExpired(token: string): boolean {
  const expiresAt = readExpiry(token);
  if (expiresAt === null) {
    return false;
  }
  return expiresAt - EXPIRY_SKEW_SECONDS <= Date.now() / 1000;
}

/** The `exp` claim in seconds, or null when the token carries no readable one. */
function readExpiry(token: string): number | null {
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims: unknown = JSON.parse(globalThis.atob(padded));
    if (typeof claims !== 'object' || claims === null) {
      return null;
    }
    const expiry = (claims as { exp?: unknown }).exp;
    return typeof expiry === 'number' && Number.isFinite(expiry) ? expiry : null;
  } catch {
    // Not decodable as a JWT payload — leave the decision to the API.
    return null;
  }
}
